import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, CheckCircle, Zap, TrendingUp, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";

const PERKS = [
  { icon: TrendingUp, text: "Top of Discover for everyone in your area" },
  { icon: Eye, text: "Up to 10× more profile views" },
  { icon: Zap, text: "Faster matches with active users" },
];

export default function Boost() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState("boost_24h");
  const [loading, setLoading] = useState(false);
  const [boostStatus, setBoostStatus] = useState(null);

  const loadData = useCallback(async () => {
    const [pkgRes, statusRes] = await Promise.all([
      fetch(`${API}/boost/packages`),
      fetch(`${API}/premium/me`, { credentials: "include" }),
    ]);
    if (pkgRes.ok) setPackages((await pkgRes.json()).packages);
    if (statusRes.ok) setBoostStatus(await statusRes.json());
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleBoost = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/boost/create-session`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ package_id: selected, origin_url: window.location.origin }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      const data = await res.json();
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #001a33 0%, #000022 100%)" }}>
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-2xl">
        <div className="text-center mb-8">
          <Rocket className="w-16 h-16 mx-auto text-[#FF00FF] mb-3" style={{ filter: "drop-shadow(0 0 20px #FF00FF)" }} />
          <h1 className="text-3xl sm:text-4xl font-bold text-[#FF00FF] mb-2" style={{ textShadow: "0 0 15px #FF00FF" }} data-testid="boost-page-title">
            Profile Boost
          </h1>
          <p className="text-[#00FFFF]">Skyrocket your visibility</p>
        </div>

        {boostStatus?.boost_active && (
          <div className="myspace-card mb-6 border-[#00FF00]" data-testid="boost-active-banner">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-[#00FF00]" />
              <div>
                <p className="text-[#00FF00] font-bold">Boost Active</p>
                <p className="text-sm text-[#99CCFF]">
                  Expires: {new Date(boostStatus.boost_expires_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="myspace-card mb-6">
          <h2 className="text-lg font-bold text-[#00FFFF] mb-4">Why boost?</h2>
          <ul className="space-y-3">
            {PERKS.map((perk) => (
              <li key={perk.title || perk.label} className="flex items-center gap-3 text-[#FF66FF]">
                <perk.icon className="w-5 h-5 text-[#FF00FF] flex-shrink-0" />
                <span>{perk.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-3 mb-6">
          {packages.map((pkg) => (
            <button
              key={pkg.id}
              onClick={() => setSelected(pkg.id)}
              className={`w-full p-4 border-2 text-left transition-all ${
                selected === pkg.id
                  ? "border-[#FF00FF] bg-[rgba(255,0,255,0.1)]"
                  : "border-[#6600CC] hover:border-[#FF00FF]"
              }`}
              data-testid={`boost-package-${pkg.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-bold text-[#00FFFF]">{pkg.label}</p>
                  <p className="text-sm text-[#99CCFF]">${pkg.amount} • {pkg.hours}h</p>
                </div>
                {selected === pkg.id && <CheckCircle className="w-6 h-6 text-[#FF00FF]" />}
              </div>
            </button>
          ))}
        </div>

        <button
          onClick={handleBoost}
          disabled={loading || !user}
          className="myspace-btn-primary w-full text-lg py-4 disabled:opacity-50"
          data-testid="buy-boost-btn"
        >
          {loading ? "Redirecting to Stripe..." : `Boost Now • $${packages.find(p => p.id === selected)?.amount || ""}`}
        </button>

        <p className="text-xs text-[#99CCFF] text-center mt-4">
          Secure payment via Stripe. Boost activates instantly.
        </p>
      </main>
    </div>
  );
}

export function BoostSuccess() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");

  const pollStatus = useCallback(async (sid, attempt) => {
    if (attempt >= 6) { setStatus("timeout"); return; }
    try {
      const res = await fetch(`${API}/boost/status/${sid}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshUser();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
      }
    } catch (e) { /* retry */ }
    setTimeout(() => pollStatus(sid, attempt + 1), 2000);
  }, [refreshUser]);

  useEffect(() => {
    if (sessionId) pollStatus(sessionId, 0);
  }, [sessionId, pollStatus]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(180deg, #001a33 0%, #000022 100%)" }}>
      <div className="myspace-card text-center max-w-md w-full">
        {status === "checking" && (
          <>
            <div className="w-12 h-12 border-4 border-[#FF00FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[#FF00FF]">Activating boost...</p>
          </>
        )}
        {status === "paid" && (
          <>
            <Rocket className="w-20 h-20 mx-auto text-[#FF00FF] mb-4" style={{ filter: "drop-shadow(0 0 20px #FF00FF)" }} />
            <h2 className="text-3xl font-bold text-[#FF00FF] mb-2" style={{ textShadow: "0 0 15px #FF00FF" }} data-testid="boost-success-title">
              Boost Activated!
            </h2>
            <p className="text-[#00FFFF] mb-6">You're now at the top of Discover.</p>
            <button onClick={() => navigate("/discover")} className="myspace-btn-primary" data-testid="boost-success-continue-btn">
              Go to Discover
            </button>
          </>
        )}
        {(status === "timeout" || status === "expired") && (
          <>
            <p className="text-[#FF6600] text-lg font-bold mb-4">Payment could not be verified</p>
            <button onClick={() => navigate("/boost")} className="myspace-btn">Try Again</button>
          </>
        )}
      </div>
    </div>
  );
}
